import {Box, Flex, P} from 'exsportia-components'
import {FC, useMemo} from 'react'

import {PublicCancelPolicy} from '../../api/venue/get-public-cancel-policy'
import {venuePolicyStretches} from '../../helpers/venue-cancel-policy'
import {useLocale, useLuxon} from '../../utils/i18n'

type Props = {
  policy: PublicCancelPolicy
  // The session's start (ms). Without it the policy is shown by name and words only.
  startMs?: number | null
}

const DATE_FORMAT = 'ccc d LLL, HH:mm'

// The venue's own cancellation policy as the customer reads it before paying: its name, the venue's words,
// and what a cancellation returns at each real time before the start.
export const VenueCancelPolicyInfo: FC<Props> = ({policy, startMs}) => {
  const {l} = useLocale()
  const luxon = useLuxon()
  const lines = useMemo(() => {
    if (!startMs || !Number.isFinite(startMs)) {
      return []
    }
    const at = (ms: number) => luxon.fromMillis(ms).toFormat(DATE_FORMAT)
    const back = (percent: number) =>
      percent >= 100
        ? l('venueCancelPolicy_allBack')
        : percent <= 0
          ? l('venueCancelPolicy_nothingBack')
          : l('venueCancelPolicy_percentBack', {value: percent})
    return venuePolicyStretches(policy, startMs).map((s) => {
      if (s.fromMs === null && s.untilMs === null) {
        return l('venueCancelPolicy_anyTime', {refund: back(s.percent)})
      }
      if (s.fromMs === null) {
        return l('venueCancelPolicy_until', {when: at(s.untilMs!), refund: back(s.percent)})
      }
      return l('venueCancelPolicy_from', {when: at(s.fromMs), refund: back(s.percent)})
    })
  }, [policy, startMs, l, luxon])

  return (
    <Flex
      flexDirection='column'
      gap='4px'
      data-testid='venue-cancel-policy-info'
    >
      <P
        fontSize='body'
        fontWeight='bold'
      >
        {policy.name}
      </P>
      {policy.customerSummary && <P fontSize='body'>{policy.customerSummary}</P>}
      {lines.length > 0 && (
        <Box>
          {lines.map((line) => (
            <P
              key={line}
              fontSize='body'
              color='text-secondary'
            >
              {line}
            </P>
          ))}
        </Box>
      )}
    </Flex>
  )
}
